/**
 * Is a company listed?
 *
 * Needed by anything that surfaces only for listed entities — today the SEBI
 * SDD, which applies to listed companies (plus intermediaries and fiduciaries),
 * not to the private companies that make up most of a CS firm's portfolio.
 *
 * Two independent signals, either of which settles it:
 *
 *   1. `company.listed` — MCA master data. The enum is 'Yes' | 'No' |
 *      'Yes — BSE' | 'Yes — NSE' | 'Yes — BSE & NSE', so the test is a
 *      leading-"yes" match, not equality. `Company.js` is the source of truth
 *      for that list.
 *   2. The CIN's first character — 'L' for listed, 'U' for unlisted. Present
 *      even when the `listed` field was never filled in, which is common on
 *      manually created companies.
 *
 * Deliberately NOT added to utils/agmDeadlineEngine.js, which holds the same CIN
 * rule: that engine is mirrored between frontend and backend and guarded by
 * engineParity.test.js, so a one-sided export there would break parity. This is
 * a UI-visibility question, not due-date arithmetic, and belongs outside it.
 */

/** 'listed' | 'unlisted' | null when the CIN is absent or malformed. */
export function cinListingClass(cin) {
  const c = String(cin || '').replace(/\s+/g, '').toUpperCase();
  if (!/^[LU][0-9]{5}[A-Z]{2}[0-9]{4}[A-Z]{3}[0-9]{6}$/.test(c)) return null;
  return c[0] === 'L' ? 'listed' : 'unlisted';
}

/**
 * True only when the company is positively known to be listed.
 *
 * Unknown reads as NOT listed, which is the safe direction here: the cost of a
 * wrong `false` is a hidden menu item on a company that could ask for it, while
 * a wrong `true` puts an insider-trading register in front of every private
 * company in the portfolio.
 */
export function isListedCompany(company) {
  if (!company) return false;
  if (/^yes/i.test(String(company.listed || '').trim())) return true;
  return cinListingClass(company.cin) === 'listed';
}

/**
 * Should the SDD module be offered for this company?
 *
 * Deliberately NOT `isListedCompany`. Listing is the common trigger, not the
 * only one: SEBI's PIT FAQ extends the obligation to intermediaries and
 * fiduciaries handling a listed company's UPSI, and IPO-bound companies
 * maintain a register before they are listed — both are unlisted at the moment
 * the duty attaches, so a listing-keyed rule hides the module from exactly the
 * users who need it.
 *
 * So the rule is "offer unless explicitly turned off": only a deliberate
 * `sddApplicable === false` hides it. The page still says when listing status
 * is not recorded, so nobody mistakes availability for a legal opinion.
 */
export function isSddApplicable(company) {
  if (!company) return false;
  if (company.sddApplicable === false) return false;
  return true;
}

/** Shown when the module is open but the company is not recorded as listed. */
export const SDD_LISTING_NOTE =
  'This company is not recorded as listed. The Structured Digital Database is ' +
  'required of listed companies, and of market intermediaries and fiduciaries ' +
  'handling their UPSI, under SEBI (Prohibition of Insider Trading) ' +
  'Regulations, 2015, Reg 3(5) — companies preparing for an IPO commonly ' +
  'maintain one too. Confirm applicability with your compliance officer.';
